import React, { useState, useCallback } from 'react';
import { invoiceAPI } from '../../../services/api';
import { PageHeader } from '../../../components/layout/PageHeader';
import { ArrowDownTrayIcon, DocumentChartBarIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';

const fmt = n => 'Rs.' + Number(n||0).toLocaleString('en-IN', { minimumFractionDigits:2 });

export function GSTReportNew() {
  const now = new Date();
  const [from,    setFrom]    = useState(new Date(now.getFullYear(), now.getMonth(), 1).toISOString().split('T')[0]);
  const [to,      setTo]      = useState(now.toISOString().split('T')[0]);
  const [gstData, setGstData] = useState([]);
  const [hsnData, setHsnData] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [gstRes, hsnRes] = await Promise.allSettled([
        invoiceAPI.reportGst({ from, to }),
        invoiceAPI.reportHsn({ from, to }),
      ]);
      if (gstRes.status === 'fulfilled') setGstData(gstRes.value.data || []);
      if (hsnRes.status === 'fulfilled') setHsnData(hsnRes.value.data || []);
    } catch { toast.error('Failed to load GST report'); }
    finally { setLoading(false); }
  }, [from, to]);

  const totalTaxable = gstData.reduce((s,r)=>s+r.taxableAmount,0);
  const totalCgst    = gstData.reduce((s,r)=>s+r.cgstAmount,0);
  const totalSgst    = gstData.reduce((s,r)=>s+r.sgstAmount,0);
  const totalIgst    = gstData.reduce((s,r)=>s+r.igstAmount,0);
  const totalTax     = gstData.reduce((s,r)=>s+r.totalTax,0);

  const inp = 'px-3 py-2 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500';
  const lbl = 'block text-xs font-semibold text-gray-500 uppercase mb-1.5';

  return (
    <div className="space-y-5">
      <PageHeader title="GST Report" subtitle="CGST, SGST, IGST summary and HSN-wise breakup for GSTR filing"/>

      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
        <div className="flex gap-4 flex-wrap items-end">
          <div><label className={lbl}>From</label><input type="date" value={from} onChange={e=>setFrom(e.target.value)} className={inp}/></div>
          <div><label className={lbl}>To</label><input type="date" value={to} onChange={e=>setTo(e.target.value)} className={inp}/></div>
          <div className="flex gap-2">
            {[
              { label:'This Month', f:new Date(now.getFullYear(),now.getMonth(),1), t:now },
              { label:'Last Month', f:new Date(now.getFullYear(),now.getMonth()-1,1), t:new Date(now.getFullYear(),now.getMonth(),0) },
              { label:'Q1 Apr-Jun', f:new Date(now.getFullYear(),3,1), t:new Date(now.getFullYear(),5,30) },
            ].map(r=>(
              <button key={r.label} onClick={()=>{setFrom(r.f.toISOString().split('T')[0]);setTo(r.t.toISOString().split('T')[0]);}}
                className="px-3 py-2 text-xs font-medium border border-gray-200 rounded-xl hover:bg-indigo-50 text-gray-600">{r.label}</button>
            ))}
          </div>
          <button onClick={load} disabled={loading} className="px-5 py-2 bg-indigo-600 text-white text-sm font-semibold rounded-xl hover:bg-indigo-700 disabled:opacity-50">
            {loading ? 'Generating...' : 'Generate'}
          </button>
          {gstData.length > 0 && (
            <button onClick={()=>window.print()} className="flex items-center gap-2 px-4 py-2 border border-gray-200 rounded-xl text-sm text-gray-600 hover:bg-gray-50">
              <ArrowDownTrayIcon className="w-4 h-4"/> Print
            </button>
          )}
        </div>
      </div>

      {!gstData.length && !loading && (
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-16 text-center">
          <DocumentChartBarIcon className="w-12 h-12 mx-auto text-gray-200 mb-3"/>
          <p className="text-gray-400 text-sm">Select period and click Generate</p>
        </div>
      )}

      {loading && <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-16 text-center">
        <div className="w-8 h-8 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin mx-auto"/></div>}

      {gstData.length > 0 && !loading && <>
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
          {[
            { label:'Taxable Amount', value:fmt(totalTaxable), color:'text-gray-800' },
            { label:'CGST',           value:fmt(totalCgst),    color:'text-blue-600' },
            { label:'SGST',           value:fmt(totalSgst),    color:'text-blue-600' },
            { label:'IGST',           value:fmt(totalIgst),    color:'text-orange-600' },
            { label:'Total GST',      value:fmt(totalTax),     color:'text-indigo-600' },
          ].map(k=>(
            <div key={k.label} className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4">
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide">{k.label}</p>
              <p className={`text-lg font-bold mt-1 ${k.color}`}>{k.value}</p>
            </div>
          ))}
        </div>

        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
          <div className="px-5 py-3 border-b border-gray-100 bg-gray-50">
            <h3 className="font-semibold text-gray-800 text-sm">GST Rate-wise Summary (Output Tax on Sales)</h3>
          </div>
          <table className="w-full text-sm">
            <thead className="bg-gray-50">
              <tr>{['GST Rate','Tax Type','Invoices','Taxable Amount','CGST','SGST','IGST','Total GST'].map(h=>(
                <th key={h} className="px-4 py-2.5 text-xs font-semibold text-gray-500 uppercase text-right first:text-left">{h}</th>
              ))}</tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {gstData.map((row,i)=>(
                <tr key={i} className="hover:bg-gray-50">
                  <td className="px-4 py-2.5 font-bold text-gray-900">{row.gstRate}%</td>
                  <td className="px-4 py-2.5 text-gray-500">{row.taxType}</td>
                  <td className="px-4 py-2.5 text-right text-gray-500">{row.invoiceCount}</td>
                  <td className="px-4 py-2.5 text-right text-gray-800">{fmt(row.taxableAmount)}</td>
                  <td className="px-4 py-2.5 text-right text-blue-600">{fmt(row.cgstAmount)}</td>
                  <td className="px-4 py-2.5 text-right text-blue-600">{fmt(row.sgstAmount)}</td>
                  <td className="px-4 py-2.5 text-right text-orange-600">{fmt(row.igstAmount)}</td>
                  <td className="px-4 py-2.5 text-right font-bold text-indigo-600">{fmt(row.totalTax)}</td>
                </tr>
              ))}
              <tr className="bg-gray-50 font-bold border-t-2 border-gray-200">
                <td className="px-4 py-2.5 text-gray-900" colSpan={3}>TOTAL</td>
                <td className="px-4 py-2.5 text-right text-gray-900">{fmt(totalTaxable)}</td>
                <td className="px-4 py-2.5 text-right text-blue-700">{fmt(totalCgst)}</td>
                <td className="px-4 py-2.5 text-right text-blue-700">{fmt(totalSgst)}</td>
                <td className="px-4 py-2.5 text-right text-orange-700">{fmt(totalIgst)}</td>
                <td className="px-4 py-2.5 text-right text-indigo-700 text-base">{fmt(totalTax)}</td>
              </tr>
            </tbody>
          </table>
        </div>

        {hsnData.length > 0 && (
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
            <div className="px-5 py-3 border-b border-gray-100 bg-gray-50">
              <h3 className="font-semibold text-gray-800 text-sm">HSN / SAC Summary — GSTR-1 Table 12</h3>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm min-w-[800px]">
                <thead className="bg-gray-50">
                  <tr>{['HSN/SAC','UOM','Total Qty','Taxable','CGST%','CGST Amt','SGST%','SGST Amt','IGST%','IGST Amt','CESS'].map(h=>(
                    <th key={h} className="px-3 py-2.5 text-xs font-semibold text-gray-500 uppercase text-right first:text-left">{h}</th>
                  ))}</tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {hsnData.map((row,i)=>(
                    <tr key={i} className="hover:bg-gray-50">
                      <td className="px-3 py-2.5 font-mono font-semibold text-gray-800">{row.hsnCode}</td>
                      <td className="px-3 py-2.5 text-right text-gray-500">{row.unit}</td>
                      <td className="px-3 py-2.5 text-right text-gray-600">{Number(row.totalQty||0).toFixed(2)}</td>
                      <td className="px-3 py-2.5 text-right text-gray-800">{fmt(row.taxableAmount)}</td>
                      <td className="px-3 py-2.5 text-right text-gray-500">{row.cgstRate}%</td>
                      <td className="px-3 py-2.5 text-right text-blue-600">{fmt(row.cgstAmount)}</td>
                      <td className="px-3 py-2.5 text-right text-gray-500">{row.sgstRate}%</td>
                      <td className="px-3 py-2.5 text-right text-blue-600">{fmt(row.sgstAmount)}</td>
                      <td className="px-3 py-2.5 text-right text-gray-500">{row.igstRate}%</td>
                      <td className="px-3 py-2.5 text-right text-orange-600">{fmt(row.igstAmount)}</td>
                      <td className="px-3 py-2.5 text-right text-gray-500">{fmt(row.cessAmount)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </>}
    </div>
  );
}
